import { yupResolver } from "@hookform/resolvers/yup"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import * as Yup from "yup"

import Button from "components/ui/button"
import Image from "components/ui/image"
import Textarea from "components/ui/textarea"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"

const MAX_CHARS = 200
const MAX_LINES = 7

const validateMsg = (message) => {
  const rows = message.split("\n")
  const newRows = rows.length > MAX_LINES ? rows.slice(0, MAX_LINES) : rows
  return newRows.join("\n")
}

const handleChange = (e, setValue) => {
  const text = e.target.value
  const keycode = e.keycode || e.which || e.charCode
  const rows = text.split("\n")
  if (
    (rows.length >= MAX_LINES && keycode === 13) ||
    (text.length >= MAX_CHARS && ![8, 46].includes(keycode))
  ) {
    e.preventDefault()
  }
  setValue("message", validateMsg(text))
}

const handleDrop = (e, setValue) => {
  e.preventDefault()
  const text = e.dataTransfer.getData("text")
  setValue("message", validateMsg(text))
}

const schema = Yup.object().shape({
  message: Yup.string().max(200, "Maximum 200 characters"),
})

const VIEW = {
  ADD: "ADD",
  EDIT: "EDIT",
}

const CustomMessage = ({
  handleGiftBundle,
  selectedGiftBundleOption,
  selectedMessage,
  setView,
}) => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      message: selectedMessage || "",
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })

  const message = watch("message")
  const charsLeft = MAX_CHARS - (message?.length || 0)

  const onSubmit = async (data) => {
    try {
      await new Promise((res, rej) => {
        handleGiftBundle(
          {
            ...selectedGiftBundleOption,
            ...data,
          },
          {
            onError: (err) => rej(err),
            onSuccess: () => {
              setView(VIEW.EDIT)
              res()
            },
          }
        )
      })
    } catch (err) {
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <form className="my-12" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="message"
        render={({ field, fieldState: { error } }) => (
          <>
            <Textarea
              className="border-none bg-white px-12 py-8"
              data-cy="cart-line-item-greeting-card-message"
              error={error}
              onDrop={(e) => handleDrop(e, setValue)}
              onKeyDown={(e) => handleChange(e, setValue)}
              onKeyUp={(e) => handleChange(e, setValue)}
              onPaste={(e) => handleChange(e, setValue)}
              placeholder="Enter your custom message."
              rows={4}
              {...field}
            />
            <p className="text-grey-400 text-12 mt-4">
              200 characters max / {charsLeft}{" "}
              {`character${charsLeft > 1 ? "s" : ""}`} left
            </p>
            {errors?.root?.error && (
              <p className="text-error">{errors.root.error.message}</p>
            )}
            <Button
              aria-label="Apply custom message"
              className="w-128 text-14 mt-16"
              disabled={isSubmitting}
              loading={isSubmitting}
              size="sm"
              type="submit"
              variant="primary"
            >
              Apply
            </Button>
          </>
        )}
      />
    </form>
  )
}

CustomMessage.propTypes = {
  handleGiftBundle: PropTypes.func,
  selectedGiftBundleOption: PropTypes.object,
  selectedMessage: PropTypes.string,
  setView: PropTypes.func,
}

const MessagePreview = ({ selectedMessage, setView }) => {
  return (
    <>
      {selectedMessage && (
        <div className="text-13 mt-16 bg-white px-16 py-12">
          {selectedMessage}
        </div>
      )}
      <Button
        className="text-13 mt-16"
        onClick={() => setView(VIEW.ADD)}
        text
        underline
        variant="secondary"
      >
        Edit or Change Custom Message
      </Button>
    </>
  )
}

MessagePreview.propTypes = {
  selectedMessage: PropTypes.string,
  setView: PropTypes.func,
}

const GiftBundleItem = ({
  giftBundle,
  handleGiftBundle,
  lineItemId,
  selected,
  selectedMessage,
  tabIndex,
}) => {
  const keyboardProps = useNavigation({ direction: "both" })

  const handleChange = useCallback(() => {
    if (!selected) {
      handleGiftBundle({ ...giftBundle, message: selectedMessage })
    }
  }, [giftBundle, handleGiftBundle, selected, selectedMessage])

  return (
    <label className="cursor-pointer">
      <input
        checked={selected}
        className="peer sr-only"
        id={`cart-line-item-gift-bundle-${lineItemId}-${giftBundle.addon_code}`}
        name={`cart-line-item-gift-option-${lineItemId}`}
        onChange={handleChange}
        tabIndex={tabIndex}
        type="radio"
        value={giftBundle.addon_code}
        {...keyboardProps}
      />
      <div className="border-grey-200 peer-checked:border-primary tablet:flex-row-reverse tablet:items-center flex h-full w-full flex-col items-start justify-between gap-8 border-2 bg-white px-16">
        <Image
          alt=""
          className="tablet:w-(--tablet-width) w-full"
          height="164"
          src={giftBundle.image_url}
          style={{ "--tablet-width": "120px" }}
          width="180"
        />
        <div className="tablet:pb-0 pb-16">
          <p className="text-14 mb-6">{giftBundle.title}</p>
          <p className="text-15 font-semibold">
            {to_currency(giftBundle.price)}
          </p>
        </div>
      </div>
    </label>
  )
}

GiftBundleItem.propTypes = {
  giftBundle: PropTypes.object,
  handleGiftBundle: PropTypes.func,
  lineItemId: PropTypes.string,
  selected: PropTypes.bool,
  selectedMessage: PropTypes.string,
  tabIndex: PropTypes.number,
}

const GiftBundles = ({ handleGiftBundle, item, selectedGiftBundle }) => {
  const giftBundleOptions = item.gift_bundle_options || []

  const selectedMessage =
    selectedGiftBundle?.custom_properties?.greeting_card_properties?.message

  const [view, setView] = useState(selectedMessage ? VIEW.EDIT : VIEW.ADD)

  const selectedGiftBundleOption = giftBundleOptions.find(
    ({ addon_code }) => addon_code === selectedGiftBundle?.addon_code
  )

  useEffect(() => {
    if (!selectedGiftBundle) {
      setView(VIEW.ADD)
    }
  }, [selectedGiftBundle])

  if (!giftBundleOptions.length) {
    return null
  }

  return (
    <>
      <FocusScope>
        <div
          aria-label="Select a gifting option of your choice."
          className="grid grid-cols-2 gap-8"
          role="radiogroup"
        >
          {giftBundleOptions.map((giftBundle, i) => {
            const selected =
              giftBundle.addon_code === selectedGiftBundle?.addon_code

            return (
              <GiftBundleItem
                giftBundle={giftBundle}
                handleGiftBundle={handleGiftBundle}
                key={`item-bundle-${giftBundle.addon_code}`}
                lineItemId={item.id}
                selected={selected}
                selectedMessage={selectedMessage}
                tabIndex={selected || (!selectedGiftBundle && i === 0) ? 0 : -1}
              />
            )
          })}
        </div>
      </FocusScope>
      {selectedGiftBundleOption &&
        selectedGiftBundleOption.can_add_message &&
        (view === VIEW.ADD ? (
          <CustomMessage
            handleGiftBundle={handleGiftBundle}
            selectedGiftBundleOption={selectedGiftBundleOption}
            selectedMessage={selectedMessage}
            setView={setView}
          />
        ) : (
          <MessagePreview selectedMessage={selectedMessage} setView={setView} />
        ))}
    </>
  )
}

GiftBundles.propTypes = {
  handleGiftBundle: PropTypes.func,
  item: PropTypes.object,
  selectedGiftBundle: PropTypes.object,
}

export default GiftBundles
